import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { observeVisibility } from './helpers';
gsap.registerPlugin(SplitText, ScrollTrigger);
const HIGHLIGHT_SELECTOR = '.js--gradient-highlight';
const PRE_REVEAL_CLASS = 'js--gradient-pre-reveal';
const INIT_ATTR = 'data-gradient-sweep-init';
const GRADIENT_COLORS = ['#664cce', '#7a3ce5', '#f86a32'];
const GRADIENT_STOPS = [3.839, 34.233, 66.546];
const CHAR_STAGGER = 0.045;
const CHAR_DURATION = 0.5;
const HIGHLIGHT_ONLY_DELAY = 0.2;
const TITLE_LINES_SELECTOR = '.js--gradient-title-lines';
const HIGHLIGHT_LINES_SELECTOR = '.js--gradient-highlight-lines';
const LINE_PRE_REVEAL_CLASS = 'js--line-pre-reveal';
const LINE_PRE_REVEAL_OPACITY = 0.5;
const LINE_STAGGER = 0.5;
const LINE_CHAR_STAGGER = 0.03;
const LINE_DURATION = 0.2;
const LINE_REVEAL_SCROLL_START = 'top 82%';
const LINE_REVEAL_SCROLL_END = 'top 45%';
const EMOJI_PATTERN = /\p{Extended_Pictographic}/u;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const hexToRgb = (hex) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
];
const rgbToHex = (r, g, b) => `#${[r, g, b]
    .map((c) => Math.round(Math.min(255, Math.max(0, c)))
    .toString(16)
    .padStart(2, '0'))
    .join('')}`;
const colorAtGradientPosition = (percent) => {
    const p = Math.min(100, Math.max(0, percent));
    if (p <= GRADIENT_STOPS[0])
        return GRADIENT_COLORS[0];
    if (p >= GRADIENT_STOPS[GRADIENT_STOPS.length - 1])
        return GRADIENT_COLORS[GRADIENT_COLORS.length - 1];
    const segment = p < GRADIENT_STOPS[1] ? 0 : 1;
    const [p0, p1] = [GRADIENT_STOPS[segment], GRADIENT_STOPS[segment + 1]];
    const [r0, g0, b0] = hexToRgb(GRADIENT_COLORS[segment]);
    const [r1, g1, b1] = hexToRgb(GRADIENT_COLORS[segment + 1]);
    const t = (p - p0) / (p1 - p0);
    return rgbToHex(r0 + (r1 - r0) * t, g0 + (g1 - g0) * t, b0 + (b1 - b0) * t);
};
const runHighlightOnlySweep = (highlight) => {
    const split = SplitText.create(highlight, {
        type: 'words, chars',
        charsClass: 'js--gradient-char',
        specialChars: /\p{Extended_Pictographic}/gu,
    });
    const targets = split.chars.filter((char) => !EMOJI_PATTERN.test(char.textContent ?? ''));
    const tl = gsap.timeline({
        delay: HIGHLIGHT_ONLY_DELAY,
        onComplete: () => {
            highlight.classList.remove(PRE_REVEAL_CLASS);
        },
    });
    targets.forEach((char, index) => {
        const relX = targets.length > 1 ? (index / (targets.length - 1)) * 100 : 0;
        const finalColor = colorAtGradientPosition(relX);
        tl.to(char, {
            keyframes: [{ color: finalColor, webkitTextFillColor: finalColor }],
            duration: CHAR_DURATION,
            ease: 'sine.inOut',
        }, index * CHAR_STAGGER);
    });
};
const initPreReveal = (el) => {
    if (el.hasAttribute(INIT_ATTR))
        return;
    el.setAttribute(INIT_ATTR, '');
    if (reducedMotion())
        return;
    el.classList.add(PRE_REVEAL_CLASS);
};
export const initGradientHighlights = () => {
    let observerBound = false;
    const init = () => {
        document.querySelectorAll(HIGHLIGHT_SELECTOR).forEach(initPreReveal);
        if (observerBound)
            return;
        observerBound = true;
        observeVisibility(HIGHLIGHT_SELECTOR, 'js--gradient-in-view', 0.4, false, '0px', (highlight) => {
            if (highlight.classList.contains(PRE_REVEAL_CLASS))
                runHighlightOnlySweep(highlight);
        });
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    }
    else {
        init();
    }
    if (window.acf) {
        window.acf.addAction('render_block_preview', init);
    }
};
const buildLineRevealTimeline = (title, highlightSelector, timelineVars = {}) => {
    const normalize = (s) => s.replace(/\s+/g, '');
    const highlightTextBefore = normalize(title.querySelector(highlightSelector)?.textContent ?? '');
    const fullTextBefore = normalize(title.textContent ?? '');
    const highlightStart = highlightTextBefore ? fullTextBefore.indexOf(highlightTextBefore) : -1;
    const highlightEnd = highlightStart >= 0 ? highlightStart + highlightTextBefore.length : -1;
    const split = SplitText.create(title, {
        type: 'lines, words, chars',
        charsClass: 'js--gradient-char',
        specialChars: /\p{Extended_Pictographic}/gu,
    });
    const chars = split.chars.filter((char) => !EMOJI_PATTERN.test(char.textContent ?? ''));
    const highlightChars = highlightStart >= 0 ? chars.slice(highlightStart, highlightEnd) : [];
    const highlightCharSet = new Set(highlightChars);
    const mainColor = getComputedStyle(title).color;
    gsap.set(chars, { opacity: LINE_PRE_REVEAL_OPACITY });
    if (highlightChars.length) {
        gsap.set(highlightChars, { color: mainColor, webkitTextFillColor: mainColor });
    }
    title.classList.remove(LINE_PRE_REVEAL_CLASS);
    gsap.set(title, { clearProps: 'opacity' });
    const tl = gsap.timeline(timelineVars);
    split.lines.forEach((line, lineIndex) => {
        const lineChars = chars.filter((char) => line.contains(char));
        const lineStart = lineIndex * LINE_STAGGER;
        lineChars.forEach((char, charIndex) => {
            const vars = {
                opacity: 1,
                duration: LINE_DURATION,
                ease: 'power4.in',
            };
            if (highlightCharSet.has(char)) {
                const hIndex = highlightChars.indexOf(char);
                const relX = highlightChars.length > 1 ? (hIndex / (highlightChars.length - 1)) * 100 : 0;
                const finalColor = colorAtGradientPosition(relX);
                vars.color = finalColor;
                vars.webkitTextFillColor = finalColor;
            }
            tl.to(char, vars, lineStart + charIndex * LINE_CHAR_STAGGER);
        });
    });
};
const runTitleLineReveal = (title) => {
    buildLineRevealTimeline(title, HIGHLIGHT_LINES_SELECTOR, {
        scrollTrigger: {
            trigger: title,
            start: LINE_REVEAL_SCROLL_START,
            end: LINE_REVEAL_SCROLL_END,
            scrub: true,
        },
    });
};
const runTitleLineRevealOnView = (title, highlightSelector) => {
    buildLineRevealTimeline(title, highlightSelector);
};
export const initGradientTitleLines = () => {
    const init = () => {
        document.querySelectorAll(TITLE_LINES_SELECTOR).forEach((title) => {
            if (title.hasAttribute(INIT_ATTR))
                return;
            title.setAttribute(INIT_ATTR, '');
            if (reducedMotion())
                return;
            title.classList.add(LINE_PRE_REVEAL_CLASS);
            runTitleLineReveal(title);
        });
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    }
    else {
        init();
    }
    window.addEventListener('load', () => ScrollTrigger.refresh());
    if (window.acf) {
        window.acf.addAction('render_block_preview', init);
    }
};
export const initTitleLineRevealOnView = (titleSelector, highlightSelector) => {
    let observerBound = false;
    const init = () => {
        document.querySelectorAll(titleSelector).forEach((title) => {
            if (title.hasAttribute(INIT_ATTR))
                return;
            title.setAttribute(INIT_ATTR, '');
            if (reducedMotion())
                return;
            title.classList.add(LINE_PRE_REVEAL_CLASS);
        });
        if (observerBound)
            return;
        observerBound = true;
        observeVisibility(titleSelector, 'js--gradient-in-view', 0.4, false, '0px', (title) => {
            if (title.classList.contains(LINE_PRE_REVEAL_CLASS)) {
                runTitleLineRevealOnView(title, highlightSelector);
            }
        });
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    }
    else {
        init();
    }
    if (window.acf) {
        window.acf.addAction('render_block_preview', init);
    }
};
